import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Trophy, TrendingUp, Maximize2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeaderboardWidget = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [level, setLevel] = useState('individual');
  const [period, setPeriod] = useState('month');
  const [metric, setMetric] = useState('revenue');
  const [fullscreenOpen, setFullscreenOpen] = useState(false);

  useEffect(() => {
    fetchLeaderboard();
  }, [level, period, metric]);

  const fetchLeaderboard = async () => {
    try {
      const response = await axios.get(`${API}/director/leaderboard`, {
        params: { level, period, metric }
      });
      setLeaderboard(response.data);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    if (amount >= 1000000) return `$${(amount / 1000000).toFixed(1)}M`;
    if (amount >= 1000) return `$${(amount / 1000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  const getMedalEmoji = (rank) => {
    switch (rank) {
      case 0: return '🥇';
      case 1: return '🥈';
      case 2: return '🥉';
      default: return `#${rank + 1}`;
    }
  };

  const getMedalBg = (rank) => {
    switch (rank) {
      case 0: return 'bg-yellow-50 border-yellow-200';
      case 1: return 'bg-gray-50 border-gray-200';
      case 2: return 'bg-orange-50 border-orange-200';
      default: return 'bg-white';
    }
  };

  const renderLeaderboardItem = (item, rank, compact = false) => (
    <div key={rank} className={`flex items-center gap-3 p-3 border rounded-lg ${getMedalBg(rank)} transition hover:shadow-md`}>
      <div className="text-2xl font-bold w-12 text-center">{getMedalEmoji(rank)}</div>
      <div className="flex-1">
        <div className="font-semibold">{item.name || 'Unknown'}</div>
        {!compact && (
          <div className="text-sm text-gray-600">
            {formatCurrency(item.revenue)} • {item.deals} deals
            {item.role && <span className="ml-2">• {item.role}</span>}
          </div>
        )}
        {compact && (
          <div className="text-xs text-gray-600">
            {formatCurrency(item.revenue)}
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-green-600">
          {metric === 'revenue' && formatCurrency(item[metric])}
          {metric === 'deals' && item[metric]}
        </div>
        {!compact && item.avg_deal_size > 0 && (
          <div className="text-xs text-gray-500">Avg: {formatCurrency(item.avg_deal_size)}</div>
        )}
      </div>
    </div>
  );

  if (loading) {
    return <div className="text-center py-8">Loading...</div>;
  }

  return (
    <>
      <div className="space-y-3">
        {/* Controls */}
        <div className="flex gap-2 flex-wrap">
          <Select value={level} onValueChange={setLevel}>
            <SelectTrigger className="w-[120px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="individual">Individuals</SelectItem>
              <SelectItem value="team">Teams</SelectItem>
              <SelectItem value="market">Markets</SelectItem>
            </SelectContent>
          </Select>

          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[110px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Week</SelectItem>
              <SelectItem value="month">Month</SelectItem>
              <SelectItem value="quarter">Quarter</SelectItem>
              <SelectItem value="year">Year</SelectItem>
            </SelectContent>
          </Select>

          <Select value={metric} onValueChange={setMetric}>
            <SelectTrigger className="w-[110px] h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Revenue</SelectItem>
              <SelectItem value="deals">Deals</SelectItem>
            </SelectContent>
          </Select>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setFullscreenOpen(true)}
            className="ml-auto"
          >
            <Maximize2 className="h-4 w-4" />
          </Button>
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 ? (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {leaderboard.slice(0, 5).map((item, idx) => renderLeaderboardItem(item, idx, true))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 border-2 border-dashed rounded-lg">
            <Trophy className="h-8 w-8 mx-auto mb-2 text-gray-400" />
            <p className="text-sm">No leaderboard data yet</p>
          </div>
        )}
      </div>

      {/* Fullscreen Dialog */}
      <Dialog open={fullscreenOpen} onOpenChange={setFullscreenOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="h-6 w-6 text-yellow-600" />
              Leaderboard - {period.charAt(0).toUpperCase() + period.slice(1)}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            {/* Full controls */}
            <div className="flex gap-2">
              <Select value={level} onValueChange={setLevel}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individuals</SelectItem>
                  <SelectItem value="team">Teams</SelectItem>
                  <SelectItem value="market">Markets</SelectItem>
                </SelectContent>
              </Select>

              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="week">This Week</SelectItem>
                  <SelectItem value="month">This Month</SelectItem>
                  <SelectItem value="quarter">This Quarter</SelectItem>
                  <SelectItem value="year">This Year</SelectItem>
                </SelectContent>
              </Select>

              <Select value={metric} onValueChange={setMetric}>
                <SelectTrigger className="w-[130px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="deals">Deals Closed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Full leaderboard */}
            <div className="space-y-2">
              {leaderboard.map((item, idx) => renderLeaderboardItem(item, idx))}
            </div>

            {leaderboard.length === 0 && (
              <div className="text-center py-12 text-gray-500">
                <Trophy className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                <p>No data available for this period</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default LeaderboardWidget;